import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, Users, FileSignature, BadgeCheck, Award, HandCoins } from "lucide-react";
import { adminInsights, zar } from "@/api";

const STATUS_LABELS = {
  received: "Received", new: "New", contacted: "Contacted", "in-progress": "In progress",
  "agreement-sent": "Agreement sent", "agreement-signed": "Agreement signed", closed: "Closed / settled",
};

export default function InsightsTab() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    setFailed(false);
    adminInsights().then(setData).catch(() => {
      setFailed(true);
      toast.error("Could not load insights");
    });
  }, []);

  useEffect(() => load(), [load]);

  if (!data) {
    return failed ? (
      <div className="bg-[#122659] border border-red-400/30 rounded-xl p-8 text-center" data-testid="insights-error">
        <p className="text-sm text-red-400 mb-4">Insights could not be loaded.</p>
        <button data-testid="insights-retry-btn" onClick={load} className="text-sm font-bold text-sky-300 hover:text-white border border-sky-300/30 rounded-md px-5 py-2.5 transition-colors">
          Try again
        </button>
      </div>
    ) : (
      <p className="text-slate-400 text-sm" data-testid="insights-loading">Loading insights…</p>
    );
  }

  const funnelRows = [
    { icon: Users, label: "Pre-qualifications", value: data.funnel.prequalifications, testid: "funnel-prequalifications" },
    { icon: FileSignature, label: "Full applications", value: data.funnel.applications, testid: "funnel-applications" },
    { icon: TrendingUp, label: "Agreements sent", value: data.funnel.agreements_sent, testid: "funnel-agreements-sent" },
    { icon: BadgeCheck, label: "Agreements signed", value: data.funnel.agreements_signed, testid: "funnel-agreements-signed" },
    { icon: Award, label: "Fully settled", value: data.funnel.settled, testid: "funnel-settled" },
  ];
  const funnelMax = Math.max(...funnelRows.map((r) => r.value), 1);
  const revenueMax = Math.max(...data.monthly_revenue.map((m) => m.total), 1);

  return (
    <div data-testid="insights-tab" className="space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-[#122659] border border-sky-300/20 rounded-xl p-8" data-testid="funnel-card">
          <h2 className="font-display text-xl font-black text-white mb-6 flex items-center gap-2">
            <TrendingUp size={20} className="text-[#F57C00]" /> Conversion funnel
          </h2>
          <div className="space-y-4">
            {funnelRows.map((r) => (
              <div key={r.label}>
                <div className="flex justify-between items-center text-sm mb-1.5">
                  <span className="text-slate-400 flex items-center gap-2"><r.icon size={14} className="text-sky-300" />{r.label}</span>
                  <span className="font-bold text-white" data-testid={r.testid}>{r.value}</span>
                </div>
                <div className="h-2.5 rounded-full bg-[#0B1F4E] overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#F57C00] to-amber-400 transition-all duration-700" style={{ width: `${(r.value / funnelMax) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-6" data-testid="active-plans-count">
            Active ownership plans right now: <strong className="text-slate-300">{data.funnel.active_plans}</strong>
          </p>
        </div>

        <div className="bg-[#122659] border border-sky-300/20 rounded-xl p-8" data-testid="revenue-card">
          <h2 className="font-display text-xl font-black text-white mb-6 flex items-center gap-2">
            <HandCoins size={20} className="text-[#F57C00]" /> Payments received — last 6 months
          </h2>
          <div className="flex items-end gap-3 h-44" data-testid="revenue-chart">
            {data.monthly_revenue.map((m) => (
              <div key={m.month} className="flex-1 h-full flex flex-col items-center gap-2" data-testid={`revenue-month-${m.month}`}>
                <span className="text-[10px] font-bold text-white shrink-0">{m.total > 0 ? zar(m.total) : "—"}</span>
                <div className="w-full flex-1 flex items-end">
                  <div className="w-full rounded-t-md bg-gradient-to-t from-[#0B1F4E] to-[#F57C00] transition-all duration-700" style={{ height: `${Math.max((m.total / revenueMax) * 100, 4)}%` }} />
                </div>
                <span className="text-[10px] text-slate-500 shrink-0">{new Date(`${m.month}-02`).toLocaleDateString("en-ZA", { month: "short" })}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-6">
            Combines online card payments and manually logged payments.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-[#122659] border border-sky-300/20 rounded-xl p-8" data-testid="status-card">
          <h2 className="font-display text-xl font-black text-white mb-6">Applications by status</h2>
          <div className="flex flex-wrap gap-2.5" data-testid="status-breakdown">
            {Object.entries(data.status_breakdown).map(([status, count]) => (
              <span key={status} data-testid={`status-chip-${status}`} className="inline-flex items-center gap-2 text-xs font-bold border border-sky-300/30 rounded-full px-4 py-2 text-slate-300">
                {STATUS_LABELS[status] || status}
                <span className="text-[#F57C00]">{count}</span>
              </span>
            ))}
            {Object.keys(data.status_breakdown).length === 0 && (
              <p className="text-sm text-slate-500">No applications yet.</p>
            )}
          </div>
        </div>

        <div className="bg-[#122659] border border-sky-300/20 rounded-xl p-8" data-testid="programme-card">
          <h2 className="font-display text-xl font-black text-white mb-6">Programme snapshot</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { label: "Referral leads", value: data.referrals.total, testid: "snapshot-referrals" },
              { label: "Referrals signed", value: data.referrals.signed, testid: "snapshot-referrals-signed" },
              { label: "Rewards pending", value: data.referrals.rewards_pending, testid: "snapshot-rewards-pending" },
              { label: "Rental bookings", value: data.rental_bookings, testid: "snapshot-rentals" },
            ].map((s) => (
              <div key={s.label} className="bg-[#0B1F4E] border border-sky-300/20 rounded-lg p-4" data-testid={s.testid}>
                <p className="font-display text-2xl font-black text-white">{s.value}</p>
                <p className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
